import axios from 'axios'
import { isEmpty } from 'lodash'
import FileSaver from 'file-saver'

import mocks from './recordings.mocks'
import { RECORDINGS_COLUMN_WIDTHS } from './recordings.constants'
import { composeContactSearchParamData, composeMostRecentSearchData } from './helpers'
import { CancelRequestError } from '../../Helpers/api'
import sortFieldApiMapping from './const/sortFieldApiMapping'

export const RecordingsRoutes = {
  allColumnsRoute: '/api/rest/recording/apps/recordingApp/field',
  userPrefsColumnsRoute: '/api/rest/ui/prefs/recordingFields',
  savedSearchRoute: '/api/rest/ui/prefs/savedSearches',
  mostRecentSearchRoute: '/api/rest/ui/prefs/mostRecentSearch',
  pageSizeRoute: '/api/rest/ui/prefs/pageSize',
  contactsRoute: '/api/rest/recording/contact',
  evalFormRoute: '/api/rest/recording/evalform',
  metadataOperatorsRoute: '/api/rest/recording/metadataoperators',
  recordingTypesRoute: '/api/rest/recording/recordingType/recordingApp',
  timeZonesRoute: '/api/rest/scheduling/timeZone',
  metadataFieldsRoute: '/api/rest/recording/metadatafields',
  organizationRoute: '/api/rest/org/common/group/permission/ViewOrg',
  surveysRoute: '/api/rest/recording/survey',
  csvRoute: '/api/rest/recording/contact/csv',
  getEvalFormsRoute: '/api/rest/recording/evalform?status=1&exclude=sections',
}

const createError = e => new Error(e.response ? e.response.data.errorMessage : e.message)

const removeLegacyData = contact => {
  const cleanContact = { ...contact }
  delete cleanContact.archiveReason
  delete cleanContact.qualityReason

  return cleanContact
}

/**
 * Generate sorting params object for API request
 *
 * @param {String} sortColId
 * @param {String} sortDirection
 * @returns {Object} Returns an empty object if sortColId or sortDirection are falsey or if a mapped sortColumn value does not exist, else returns { sortColumn1, sortDirection1 }
 */
const generateSortParams = (sortColId, sortDirection) => {
  if (!sortColId || !sortDirection) {
    return {}
  }
  // Check if sortColId is a metadata category (category is first part of id string followed by key) and if so then use the key portion of the string to create the sort column value
  const sortColumnValue = sortColId.indexOf('metadata_fields_') === 0 ? `metadata.${sortColId.substring(16)}` : sortFieldApiMapping[sortColId] || null
  return sortDirection && sortColumnValue
    ? {
      sortColumn1: sortColumnValue,
      sortDirection1: sortDirection === 'asc' ? 'ascending' : 'descending',
    }
    : {}
}

const generateCursorParam = cursor => {
  if (!cursor) {
    return {}
  }
  return {
    cursor,
  }
}

export default {
  hashToParamString(hash) {
    const excluded = ['searchType']
    const recordingFlags = ['hr', 'training', 'tagged']

    return Object.entries(hash)
      .filter(([key]) => {
        return !excluded.includes(key)
      })
      .reduce((paramArray, [key, value]) => {
        if (value === true && !recordingFlags.includes(key)) {
          paramArray.push(key)
        } else if (Array.isArray(value)) {
          value.forEach(item => paramArray.push(`${key}=${item}`))
        } else {
          paramArray.push(`${key}=${encodeURIComponent(value)}`)
        }
        return paramArray
      }, [])
      .join('&')
  },
  async getMetadataFields() {
    try {
      const response = await axios.get(RecordingsRoutes.metadataFieldsRoute)
      return response.data
    } catch (e) {
      throw createError(e)
    }
  },
  async getAllColumns() {
    try {
      const response = await axios.get(RecordingsRoutes.allColumnsRoute)
      return response.data
    } catch (e) {
      throw createError(e)
    }
  },
  async getUserPrefsColumns() {
    try {
      const response = await axios.get(RecordingsRoutes.userPrefsColumnsRoute)
      return isEmpty(response.data) ? { columnData: [] } : response.data
    } catch (e) {
      throw createError(e)
    }
  },
  async saveUserPrefsColumns(data) {
    try {
      const response = await axios.post(RecordingsRoutes.userPrefsColumnsRoute, data)
      return response.data
    } catch (e) {
      throw createError(e)
    }
  },

  async getMostRecentSearch() {
    try {
      const response = await axios.get(RecordingsRoutes.mostRecentSearchRoute)
      return response.data
    } catch (e) {
      throw createError(e)
    }
  },

  async getPageSize() {
    try {
      const response = await axios.get(RecordingsRoutes.pageSizeRoute)
      return response.data
    } catch (e) {
      throw createError(e)
    }
  },

  async updateContact(contact) {
    const cleanContact = removeLegacyData(contact)
    try {
      await axios.put(`${RecordingsRoutes.contactsRoute}/${cleanContact.id}`, cleanContact)
      return cleanContact // api doesn't return a response
    } catch (e) {
      throw createError(e)
    }
  },

  async getContacts(params, { rangeTop, rangeBottom, order, orderBy }, cancel, pageCursor) {
    const defaultParams = {
      expand: ['metadata', 'eventCalculations'],
      reason: 'recorded',
    }

    const routeParams = {
      ...defaultParams,
      ...composeContactSearchParamData(params),
      ...generateSortParams(orderBy, order),
      ...generateCursorParam(pageCursor),
    }

    const headers = {}
    if (rangeTop && rangeBottom) {
      headers['Range'] = `items=${rangeBottom - 1}-${rangeTop - 1}`
    }

    try {
      const response = await axios.get(`${RecordingsRoutes.contactsRoute}?${this.hashToParamString(routeParams)}`, {
        cancelToken: cancel.token,
        headers,
      })

      return {
        contacts: response.data,
        nextPageCursor: response.headers['x-next-page-cursor'],
      }

    } catch (e) {
      if (axios.isCancel(e)) {
        throw new CancelRequestError(e.message)
      } else {
        throw createError(e)
      }
    }
  },

  async getContactsFromQueryParams(queryParams, { rangeTop, rangeBottom, order, orderBy }, cancel, pageCursor) {
    const defaultParams = {
      expand: ['metadata', 'eventCalculations'],
      reason: 'recorded',
    }

    const routeParams = {
      ...defaultParams,
      ...queryParams,
      ...generateSortParams(orderBy, order),
      ...generateCursorParam(pageCursor),
    }

    const headers = {}
    if (rangeTop && rangeBottom) {
      headers['Range'] = `items=${rangeBottom - 1}-${rangeTop - 1}`
    }

    try {
      const response = await axios.get(`${RecordingsRoutes.contactsRoute}?${this.hashToParamString(routeParams)}`, {
        cancelToken: cancel.token,
        headers,
      })

      return {
        contacts: response.data,
        nextPageCursor: response.headers['x-next-page-cursor'],
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        throw new CancelRequestError(e.message)
      } else {
        throw createError(e)
      }
    }
  },

  async getContactsStats(params, cancel) {
    const defaultParams = {
      expand: ['metadata', 'eventCalculations'],
      reason: 'recorded',
    }

    const routeParams = {
      ...defaultParams,
      ...composeContactSearchParamData(params),
      searchStats: 'true',
    }

    try {
      const response = await axios.get(`${RecordingsRoutes.contactsRoute}?${this.hashToParamString(routeParams)}`, {
        cancelToken: cancel.token,
      })

      return parseContactStats(response.data)
    } catch (e) {
      if (axios.isCancel(e)) {
        throw new CancelRequestError(e.message)
      } else {
        throw createError(e)
      }
    }
  },

  async postMostRecentSearch(params) {
    const payload = composeMostRecentSearchData(params)
    try {
      const response = await axios.post(RecordingsRoutes.mostRecentSearchRoute, payload)

      return response.data
    } catch (e) {
      throw createError(e)
    }
  },

  async postPageSize(pageSize) {
    const payload = { pageSize }
    try {
      const response = await axios.post(RecordingsRoutes.pageSizeRoute, payload)

      return response.data
    } catch (e) {
      throw createError(e)
    }
  },

  getColumnWidthsFromLocalStorage: () => {
    const recordingsColumnWidths = localStorage.getItem(RECORDINGS_COLUMN_WIDTHS)
    return recordingsColumnWidths ? JSON.parse(recordingsColumnWidths) : {}
  },

  saveColumnWidthsToLocalStorage: updatedColumnWidths => {
    localStorage.setItem(RECORDINGS_COLUMN_WIDTHS, JSON.stringify(updatedColumnWidths))
    return updatedColumnWidths
  },

  async downloadCSV(params) {
    const defaultParams = {
      expand: ['metadata', 'eventCalculations'],
      reason: 'recorded',
    }

    const routeParams = {
      ...defaultParams,
      ...composeContactSearchParamData(params),
    }

    const url = `${RecordingsRoutes.csvRoute}?${this.hashToParamString(routeParams)}`
    const response = await axios({
      url,
      method: 'GET',
      responseType: 'blob',
    })

    saveFile(response.data, 'contacts.csv')
  },
  // one call to rule them all
  async getActiveEvalForms(cancelToken) {
    try {
      const response = await axios.get(RecordingsRoutes.getEvalFormsRoute, { cancelToken })

      return response.data
    } catch (error) {
      if (!axios.isCancel(error)) {
        throw new Error(error)
      }
    }
  },
}

function parseContactStats(stats) {
  return {
    ...stats,
    count: parseInt(stats.count, 10),
    avgScore: parseFloat(stats.avgScore),
    avgCallDuration: parseInt(stats.avgCallDuration, 10),
  }
}

function saveFile(data, fileName) {
  FileSaver.saveAs(new Blob([data]), fileName)
}

export const mock = {
  getMetadataFields() {
    return mocks.metadataFields
  },
  getAllColumns() {
    return mocks.columns
  },
  getUserPrefsColumns() {
    return mocks.userPrefsColumns
  },
}
